import { locale, t } from '../i18n/index.ts';

// Küçük DOM yardımcıları. Çerçeve yok: araç ve TV tarayıcılarında taşınacak kod
// ne kadar azsa, oynatıcı yolunda o kadar az sürpriz var.

type Child = Node | string | number | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Partial<Record<string, unknown>> = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(props)) {
    if (value === null || value === undefined || value === false) continue;
    if (key === 'class') node.className = String(value);
    else if (key === 'dataset') Object.assign(node.dataset, value as Record<string, string>);
    else if (key === 'style' && typeof value === 'object') Object.assign(node.style, value as object);
    else if (key.startsWith('on') && typeof value === 'function') {
      node.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
    } else if (key === 'html') node.innerHTML = String(value);
    else if (key in node && key !== 'list') (node as any)[key] = value;
    else node.setAttribute(key, String(value));
  }
  append(node, children);
  return node;
}

export function append(parent: Node, children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.appendChild(typeof child === 'object' ? child : document.createTextNode(String(child)));
  }
}

export function clear(node: Element): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function mount(parent: Element, ...children: Child[]): void {
  clear(parent);
  append(parent, children);
}

const TAP_ECHO_MS = 700;
let lastTapAt = -TAP_ECHO_MS;

/**
 * Dokunma dostu tıklama: parmak birkaç piksel kaysa bile çalışsın diye
 * pointerdown/up farkına bakar. Kiosk webview'leri kaydırma kabında sentetik
 * 'click' olayını yutabiliyor (canvas-play'de gerçek bir hataydı).
 * Klavye ve kumanda için Enter/Space da bağlanır.
 */
export function onTap(node: HTMLElement, handler: (event: PointerEvent | KeyboardEvent) => void): void {
  let sx = 0;
  let sy = 0;
  let tracking = false;
  const fire = (e: PointerEvent) => { lastTapAt = performance.now(); handler(e); };
  node.addEventListener('pointerdown', (e) => { sx = e.clientX; sy = e.clientY; tracking = true; });
  node.addEventListener('pointerup', (e) => {
    if (!tracking) return;
    tracking = false;
    if (Math.abs(e.clientX - sx) < 12 && Math.abs(e.clientY - sy) < 12) fire(e);
  });
  node.addEventListener('pointercancel', () => { tracking = false; });
  // Yedek yol: tarayıcının kendi 'click' olayı. Titreşen araç ekranında parmak
  // 12 pikselden fazla kayabiliyor; tarayıcı bunu yine dokunuş sayıp click
  // üretiyor ama yukarıdaki yol reddediyordu ve düğme "çalışmıyor" görünüyordu.
  // Az önce HERHANGİ bir düğme pointerup ile işlendiyse click yok sayılır: aynı
  // dokunuşun click'i hem çift tetikleme olurdu hem de sayfa değiştiyse yeni
  // ekranda parmağın altına gelen düğmeye gidebilirdi.
  node.addEventListener('click', (e) => {
    if (performance.now() - lastTapAt < TAP_ECHO_MS) return;
    fire(e as PointerEvent);
  });
  node.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handler(e); }
  });
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '';
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function formatClock(ms: number): string {
  return new Date(ms).toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit' });
}

export function formatDate(ms: number | null): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString(locale(), { day: 'numeric', month: 'long', year: 'numeric' });
}

export function relativeTime(ms: number | null): string {
  if (!ms) return t('common.never');
  const diff = Date.now() - ms;
  if (diff < 60_000) return t('common.justNow');
  if (diff < 3600_000) return t('common.minutesAgo', { n: Math.floor(diff / 60_000) });
  if (diff < 86400_000) return t('common.hoursAgo', { n: Math.floor(diff / 3600_000) });
  return t('common.daysAgo', { n: Math.floor(diff / 86400_000) });
}
